package at.theatervogelweide.admin.web.components

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.SideEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import kotlinx.browser.window
import org.w3c.dom.events.Event

/**
 * "Unsaved changes, really switch?" for the whole app: an editor reports its changes with [ReportUnsavedChanges],
 * and everything that would drop them (selecting another row, the navigation, the browser's back button, signing out)
 * goes through [proceed]. [UnsavedChangesDialog] asks in the page; closing or reloading the tab asks through the
 * browser.
 */
class UnsavedChangesGuard {
    var hasChanges: Boolean by mutableStateOf(false)
        internal set

    internal var pending: (() -> Unit)? by mutableStateOf(null)

    /** What the reporting editor drops when the person confirms, for changes that would otherwise still be sent. */
    internal var discard: (() -> Unit)? = null

    /** Runs [action] now without unsaved changes, otherwise once the person confirms dropping them. */
    fun proceed(action: () -> Unit) {
        if (hasChanges) {
            pending = action
        } else {
            action()
        }
    }

    internal fun confirm() {
        val action = pending ?: return
        pending = null
        // the editor that had the changes is about to go; it reports again if it stays
        hasChanges = false
        discard?.also { discard = null }?.invoke()
        action()
    }

    internal fun cancel() {
        pending = null
    }
}

/**
 * Tells [guard] whether this editor has unsaved changes, for as long as it is shown. [onDiscard] drops them when the
 * person confirms dropping them, for changes that are on their way to the server, such as a debounced reorder.
 */
@Composable
fun ReportUnsavedChanges(guard: UnsavedChangesGuard, hasChanges: Boolean, onDiscard: (() -> Unit)? = null) {
    SideEffect {
        guard.hasChanges = hasChanges
        guard.discard = onDiscard.takeIf { hasChanges }
    }
    DisposableEffect(guard) {
        onDispose {
            guard.hasChanges = false
            guard.discard = null
        }
    }
}

/** The question [UnsavedChangesGuard.proceed] asks, and the browser's own question before the tab closes or reloads. */
@Composable
fun UnsavedChangesDialog(guard: UnsavedChangesGuard) {
    DisposableEffect(guard.hasChanges) {
        val listener: (Event) -> Unit = { event ->
            event.preventDefault()
            // older browsers only ask with a return value
            event.asDynamic().returnValue = ""
        }
        if (guard.hasChanges) {
            window.addEventListener("beforeunload", listener)
        }
        onDispose { window.removeEventListener("beforeunload", listener) }
    }

    AdminConfirmDialog(
        open = guard.pending != null,
        id = "ungespeicherte-aenderungen",
        title = "Ungespeicherte Änderungen",
        message = "Deine Änderungen sind noch nicht gespeichert. Wenn du jetzt wechselst, gehen sie verloren.",
        confirmLabel = "Verwerfen und wechseln",
        cancelLabel = "Weiter bearbeiten",
        onConfirm = guard::confirm,
        onCancel = guard::cancel,
        danger = true,
    )
}
